import { useMemo, useState } from 'react';

import { MultiSelect } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { NodeFilters, useFilterContext } from '@/context/FilterProvider';
import { useSearchFacets } from '@/context/SearchFacetsProvider';
import { useAccountTags } from '@/hooks/discovery/accountTags';
import { useTags } from '@/hooks/discovery/tags';
import { FilterSection } from './FilterSection';

export const tagsName = 'tags' satisfies keyof NodeFilters;

export const TagFilters = () => {
  const { accountId } = useDbtCloudAccount();
  const { environmentId } = useDbtCloudProject();
  const filterCtx = useFilterContext();
  const { aggregations } = useSearchFacets();

  const [loadTags, setLoadTags] = useState(false);

  const [uniqueTags] = useTags({
    environmentId,
    pause: !loadTags,
  });

  const [uniqueAccountTags] = useAccountTags({ accountId, pause: !loadTags });

  const tagData = environmentId
    ? uniqueTags?.data?.environment?.applied?.tags
    : uniqueAccountTags.data?.account.tags;

  const options = useMemo(() => {
    return (
      tagData?.map((tag) => {
        const tagName = tag?.name || '';
        // Backend returns tag names in lowercase, so do case-insensitive lookup
        const count = aggregations?.tags?.[tagName.toLowerCase()];
        const label = count !== undefined ? `${tagName} (${count})` : tagName;
        return {
          label,
          value: tagName || -1,
        };
      }) || []
    );
  }, [tagData, aggregations]);
  return (
    <FilterSection title="Tags">
      <div
        onFocus={(e) => {
          e.preventDefault();
          setLoadTags(true);
        }}
      >
        <MultiSelect
          name={tagsName}
          placeholder="Find a tag"
          isEdit
          options={options}
          showSelectAll={options.length > 1}
          onChange={(selectedOptions) => {
            const tags = filterCtx.filters[tagsName] ?? [];
            const values = selectedOptions.map((option) => option.value.toString());
            const itemsToAdd = values.filter((tag) => !tags.includes(tag));
            const itemsToDelete = tags.filter((tag) => !values.includes(tag));

            const isAddOperation = true;
            if (itemsToAdd.length) {
              filterCtx.updateFilters({ [tagsName]: itemsToAdd }, isAddOperation);
            }
            if (itemsToDelete.length) {
              filterCtx.updateFilters({ [tagsName]: itemsToDelete }, !isAddOperation);
            }
          }}
          className="mt-2"
        />
      </div>
    </FilterSection>
  );
};
